import { DollarSign } from 'lucide-react'
import React, { useEffect, useState } from 'react'
import { useDebounceValue } from 'usehooks-ts'

import Tooltip from 'components/reusable/Tooltip'
import { DateInput, NumberMask, TextInput } from 'slab'

import LedgerAccountInfo from './LedgerAccountInfo'
import LedgerAccountList from './LedgerAccountList'
import LedgerEntryList from './LedgerEntryList'

function LedgerExplorerImpl () {
  // Initialize from URL params right away to prevent flicker
  const initParams = new URLSearchParams(window.location.search)
  const initIdParam = initParams.get('id')
  const initQueryParam = initParams.get('query')
  const initCutoffParam = initParams.get('cutoff')
  const initMinBalanceParam = initParams.get('minBalance')
  const initMaxBalanceParam = initParams.get('maxBalance')
  const initHideZeroParam = initParams.get('hideZero')

  const [ledgerAccountId, setLedgerAccountId] = useState<string | undefined>(initIdParam || undefined)
  const [query, setQuery] = useState<string | undefined>(initQueryParam || undefined)
  const [cutoff, setCutoff] = useState<string | undefined>(initCutoffParam || undefined)
  const [minBalance, setMinBalance] = useState<string | undefined>(initMinBalanceParam || undefined)
  const [maxBalance, setMaxBalance] = useState<string | undefined>(initMaxBalanceParam || undefined)
  const [hideZero, setHideZero] = useState<boolean>(initHideZeroParam === 'true')

  useEffect(() => {
    const url = new URL(window.location.href)
    if (ledgerAccountId && ledgerAccountId !== '') {
      url.searchParams.set('id', ledgerAccountId)
    } else {
      url.searchParams.delete('id')
    }
    if (query && query !== '') {
      url.searchParams.set('query', query)
    } else {
      url.searchParams.delete('query')
    }
    if (cutoff && cutoff !== '') {
      url.searchParams.set('cutoff', cutoff)
    } else {
      url.searchParams.delete('cutoff')
    }
    if (minBalance && minBalance !== '') {
      url.searchParams.set('minBalance', minBalance)
    } else {
      url.searchParams.delete('minBalance')
    }
    if (maxBalance && maxBalance !== '') {
      url.searchParams.set('maxBalance', maxBalance)
    } else {
      url.searchParams.delete('maxBalance')
    }
    if (hideZero) {
      url.searchParams.set('hideZero', 'true')
    } else {
      url.searchParams.delete('hideZero')
    }
    // Use replaceState instead of pushState to avoid polluting browser history
    window.history.replaceState({}, '', url)
  }, [ledgerAccountId, query, cutoff, minBalance, maxBalance, hideZero])

  const [debouncedLedgerAccountId] = useDebounceValue(ledgerAccountId, 500)
  const [debouncedQuery] = useDebounceValue(query, 500)
  const [debouncedCutoff] = useDebounceValue(cutoff, 500)
  const [debouncedMinBalance] = useDebounceValue(minBalance, 500)
  const [debouncedMaxBalance] = useDebounceValue(maxBalance, 500)

  const ledgerAccountIds = debouncedLedgerAccountId?.split(',')
    .map((id) => id.trim()).filter((id) => id !== '') || []

  return (
    <>
      <div className='flex flex-col gap-4 mb-4'>
        <div className='flex items-center w-full gap-x-2'>
          <Tooltip triggerChildren={
            <div className='flex-1'>
              <TextInput
                placeholder='ID'
                disabled={!!query}
                value={ledgerAccountId || ''}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setLedgerAccountId(e.target.value || undefined)
                }}
                style={{ paddingRight: '1.75rem' }}
              />
            </div>
        }
          >
            <div className='text-xs p-2'>
              Search by ledger account ID (comma-separated for multiple)
            </div>
          </Tooltip>
          <Tooltip triggerChildren={
            <div className='flex-1'>
              <TextInput
                placeholder='Query'
                disabled={!!ledgerAccountId}
                value={query || ''}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setQuery(e.target.value || undefined)
                }}
                style={{ paddingRight: '1.75rem' }}
              />
            </div>
        }
          >
            <div className='text-xs p-2'>
              Search accounts by path (e.g., "employer" or "bank.deposits")
            </div>
          </Tooltip>
          <Tooltip triggerChildren={
            <div className='flex-1'>
              <DateInput
                placeholder='Cutoff'
                value={cutoff || ''}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setCutoff(e.target.value || undefined)
                }}
                style={{ paddingRight: '1.75rem' }}
              />
            </div>
        }
          >
            <div className='text-xs p-2'>
              Only show entries and balances up to this date
            </div>
          </Tooltip>
        </div>

        <div className='flex items-center w-full gap-x-2'>
          <Tooltip triggerChildren={
            <div className='flex-1'>
              <NumberMask decimalPlaces={2}>
                <TextInput
                  placeholder='Min Balance'
                  leadingIcon={DollarSign}
                  value={minBalance || ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const value = e.target.value.replace(/[^\d.]/g, '')
                    setMinBalance(value || undefined)
                  }}
                  style={{ paddingRight: '1.75rem' }}
                />
              </NumberMask>
            </div>
          }
          >
            <div className='text-xs p-2'>
              Only show accounts with balance greater than or equal to this amount
            </div>
          </Tooltip>
          <Tooltip triggerChildren={
            <div className='flex-1'>
              <NumberMask decimalPlaces={2}>
                <TextInput
                  placeholder='Max Balance'
                  leadingIcon={DollarSign}
                  value={maxBalance || ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const value = e.target.value.replace(/[^\d.]/g, '')
                    setMaxBalance(value || undefined)
                  }}
                  style={{ paddingRight: '1.75rem' }}
                />
              </NumberMask>
            </div>
          }
          >
            <div className='text-xs p-2'>
              Only show accounts with balance less than or equal to this amount
            </div>
          </Tooltip>
          <Tooltip triggerChildren={
            <label className='flex items-center gap-x-2 whitespace-nowrap cursor-pointer'>
              <input
                type='checkbox'
                checked={hideZero}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setHideZero(e.target.checked)
                }}
                className='rounded border-gray-300'
              />
              <span className='text-sm text-gray-700'>Hide Zero Balance</span>
            </label>
          }
          >
            <div className='text-xs p-2'>
              Hide accounts with exactly $0.00 balance
            </div>
          </Tooltip>
        </div>
      </div>

      {
        ledgerAccountIds.map((id) => (
          <LedgerAccountInfo key={id} ledgerAccountId={id} cutoff={debouncedCutoff} />
        ))
      }

      <div className='flex flex-col space-y-6'>
        <div>
          <h2 className='text-lg font-semibold mb-3'>Ledger Accounts</h2>
          <LedgerAccountList
            ledgerAccountId={debouncedLedgerAccountId}
            query={debouncedQuery}
            cutoff={debouncedCutoff}
            minBalance={debouncedMinBalance}
            maxBalance={debouncedMaxBalance}
            hideZero={hideZero}
          />
        </div>
        <div>
          <h2 className='text-lg font-semibold mb-3'>Ledger Entries</h2>
          <LedgerEntryList ledgerAccountId={debouncedLedgerAccountId} ledgerAccountQuery={debouncedQuery} cutoff={debouncedCutoff} />
        </div>
      </div>
    </>
  )
}

const LedgerExplorer = () => {
  return (
    <LedgerExplorerImpl />
  )
}

export default LedgerExplorer
